import dayjs from "dayjs";

import { SERPSnapshotResultCell } from "app/pages/keyword-research/keyword-analysis/pages/serp-snapshot/components/cells/SERPSnapshotResultCell";
import { FORMAT_VISUAL } from "app/pages/keyword-research/website-analysis/pages/ranking-distribution/Constants";
import { PositionAbsoluteChangeCell } from "app/pages/keyword-research/website-analysis/pages/ranking-distribution/PositionAbsoluteChangeCell";
import { SERPPositionCell } from "app/components/React/Table/cells/SERPPositionCell";
import { DefaultCellHeader, DefaultCellHeaderRightAlign } from "app/components/React/Table/headerCells";
import { i18nFilter } from "app/filters/i18nFilter";
import type { ISortDirection } from "app/@types/userdata";
import { invertSortDirection } from "app/UtilitiesAndConstants/UtilityFunctions/sort";

import { SERPSnapshotDomainCell } from "./components/cells/SERPSnapshotDomainCell";

const i18n = i18nFilter();

interface IColumnsConfigArguments {
    sortedColumn: string;
    sortDirection: ISortDirection;
    currentMonth: string;
    tableName?: string;
}

type InnerTableTopColumnConfigArguments = Omit<IColumnsConfigArguments, "currentMonth">;
const TABLE_WITHOUT_SITE_COLUMN = ["popular_products"];
export const tableColumns = {
    getColumnsConfig: ({ sortedColumn, sortDirection, currentMonth }: IColumnsConfigArguments) => {
        const columns = [
            {
                field: "position",
                trackingName: "Position",
                displayName: i18n("keyword.research.serp.snapshot.columns.position"),
                headerComponent: DefaultCellHeader,
                tooltip: i18n("keyword.research.serp.snapshot.columns.position.tooltip", {
                    current: dayjs.utc(currentMonth).format(FORMAT_VISUAL),
                }),
                cellComponent: SERPPositionCell,
                sortable: true,
                sortDirection: "asc",
                width: 98,
                cellClass: "serp-snapshot-table-cell right-alignment",
                inverted: "True",
            },
            {
                field: "Change",
                trackingName: "Change",
                displayName: i18n("keyword.research.serp.snapshot.columns.change"),
                headerComponent: DefaultCellHeaderRightAlign,
                tooltip: i18n("keyword.research.serp.snapshot.columns.change.tooltip", {
                    current: dayjs.utc(currentMonth).format(FORMAT_VISUAL),
                    previous: dayjs.utc(currentMonth).subtract(1, "month").format(FORMAT_VISUAL),
                }),
                cellComponent: PositionAbsoluteChangeCell,
                width: 114,
                cellClass: "serp-snapshot-table-cell right-alignment",
                showTooltip: true,
            },
            {
                field: "url",
                trackingName: "SerpSnapshotResult",
                tooltip: i18n("keyword.research.serp.snapshot.columns.url.tooltip"),
                displayName: i18n("keyword.research.serp.snapshot.columns.url"),
                headerComponent: DefaultCellHeader,
                cellComponent: SERPSnapshotResultCell,
                minWidth: 450,
                cellClass: "serp-snapshot-table-cell",
            },
            {
                field: "site",
                trackingName: "SerpSnapshotDomain",
                tooltip: i18n("keyword.research.serp.snapshot.columns.site.tooltip"),
                displayName: i18n("keyword.research.serp.snapshot.columns.domain"),
                headerComponent: DefaultCellHeader,
                cellComponent: SERPSnapshotDomainCell,
                width: 225,
                cellClass: "serp-snapshot-table-cell",
            },
        ].filter((column) => !!column);

        return columns.map<any>((col: any) => {
            const isSorted = sortedColumn && col.field === sortedColumn;
            return {
                ...col,
                visible: true,
                sortable: col.sortable || false,
                headerComponent: col.headerComponent || DefaultCellHeader,
                isSorted,
                sortDirection: isSorted ? (col.inverted ? invertSortDirection(sortDirection) : sortDirection) : "desc",
            };
        });
    },
    getWidgetTableColumnsConfig: ({
        sortedColumn,
        sortDirection,
        tableName = "",
    }: InnerTableTopColumnConfigArguments) => {
        const showSiteColumn = !TABLE_WITHOUT_SITE_COLUMN.includes(tableName);
        const columns = [
            {
                field: "position",
                trackingName: "Position",
                displayName: i18n("keyword.research.serp.snapshot.columns.position"),
                tooltip: i18n("keyword.research.serp.snapshot.top.table.columns.position.tooltip"),
                headerComponent: DefaultCellHeader,
                cellComponent: SERPPositionCell,
                sortable: true,
                sortDirection: "asc",
                width: 92,
                inverted: "True",
                cellClass: "serp-snapshot-table-cell",
            },
            {
                field: "Change",
                trackingName: "Change",
                displayName: i18n("keyword.research.serp.snapshot.columns.change"),
                tooltip: i18n("keyword.research.serp.snapshot.top.table.columns.change.tooltip"),
                headerComponent: DefaultCellHeaderRightAlign,
                cellComponent: PositionAbsoluteChangeCell,
                width: 114,
                showTooltip: true,
                cellClass: "serp-snapshot-table-cell",
            },
            {
                field: "url",
                trackingName: "Featured URL",
                displayName: i18n("keyword.research.serp.snapshot.columns.url"),
                tooltip: i18n("keyword.research.serp.snapshot.top.table.columns.url.tooltip"),
                headerComponent: DefaultCellHeader,
                cellComponent: SERPSnapshotResultCell,
                minWidth: 120,
                hideUrl: !showSiteColumn,
                cellClass: "serp-snapshot-table-cell",
            },
            showSiteColumn && {
                field: "site",
                trackingName: "SerpSnapshotDomain",
                displayName: i18n("keyword.research.serp.snapshot.columns.domain"),
                tooltip: i18n("keyword.research.serp.snapshot.top.table.columns.site.tooltip"),
                headerComponent: DefaultCellHeader,
                cellComponent: SERPSnapshotDomainCell,
                width: 225,
                cellClass: "serp-snapshot-table-cell",
            },
        ].filter((column) => !!column);

        return columns.map<any>((col: any) => {
            const isSorted = sortedColumn && col.field === sortedColumn;
            return {
                ...col,
                visible: true,
                sortable: col.sortable || false,
                headerComponent: col.headerComponent || DefaultCellHeader,
                isSorted,
                sortDirection: isSorted ? (col.inverted ? invertSortDirection(sortDirection) : sortDirection) : "desc",
            };
        });
    },
};
